// ─────────────────────────────────────────────────────────────────────────────
// Fuseau horaire applicatif (America/Toronto) et conversions heure murale ⇆ UTC.
//
// Le serveur de prod tourne en UTC : un <input type="datetime-local"> renvoie une
// heure murale sans fuseau ('YYYY-MM-DDTHH:MM') qu'il ne faut PAS passer à
// `new Date()` (qui l'interprète dans le fuseau du serveur = UTC). On convertit
// explicitement depuis/vers America/Toronto. Fonctions pures → testables.
// ─────────────────────────────────────────────────────────────────────────────

export const APP_TIME_ZONE = 'America/Toronto'

// Décalage (ms) entre APP_TIME_ZONE et UTC à l'instant donné (gère l'heure d'été).
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts = dtf.formatToParts(instant)
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value)
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  )
  return asUtc - instant.getTime()
}

// 'YYYY-MM-DDTHH:MM' (heure murale America/Toronto) → instant UTC ISO.
// Renvoie null si la chaîne est invalide.
export function wallTimeToUtcIso(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local.trim())
  if (!m) return null
  const [y, mo, d, h, mi] = [m[1], m[2], m[3], m[4], m[5]].map(Number)
  // Interprétation naïve (comme si l'heure murale était UTC), puis correction
  // par le décalage du fuseau à cet instant : T = naïf − offset.
  const naiveUtc = Date.UTC(y, mo - 1, d, h, mi)
  const offset = zoneOffsetMs(new Date(naiveUtc), APP_TIME_ZONE)
  const instant = new Date(naiveUtc - offset)
  if (Number.isNaN(instant.getTime())) return null
  return instant.toISOString()
}

// Instant UTC ISO → 'YYYY-MM-DDTHH:MM' en heure murale America/Toronto,
// pour pré-remplir un <input type="datetime-local">.
export function utcToWallTime(iso: string): string {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const parts = dtf.formatToParts(new Date(iso))
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

const zonedDateTimeFmt = new Intl.DateTimeFormat('fr-CA', {
  timeZone: APP_TIME_ZONE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

// Affichage in-app d'un instant UTC ISO, formaté en heure de Montréal.
export function formatZonedDateTime(iso: string): string {
  return zonedDateTimeFmt.format(new Date(iso))
}
